/**
 * The sales tax rate — one for cash, one for card — and whether it is charged.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * The rate lives in the database and there was no way to see it, let alone
 * change it. A rate that only a developer can change is a rate that does not
 * get changed — and when a province moves it, every bill printed until somebody
 * is available is wrong.
 *
 * WHY TWO RATES
 * -------------
 * Here a bill settled in cash carries the full provincial sales tax, and one
 * paid by card carries a lower rate the province allows for digital payments.
 * So the screen shows both, each on its own. Online is not a third rate — it
 * settles like cash and is taxed like cash.
 *
 * A shop that has not split its rates yet has a single rule that stands for
 * both tenders; changing it changes both, until a separate card rate is set up.
 *
 * WHY TAX CAN BE TURNED OFF
 * -------------------------
 * A shop may decide to stop charging sales tax on one tender or on both — and
 * when it does, the bill must show no tax at all, not a zero line. Turning a
 * tender off holds its rule aside rather than deleting it, so the rate is
 * remembered and turning it back on charges exactly what it did before. From
 * the next order, bills on that tender carry no tax and print none.
 *
 * WHY CHANGING ANYTHING ASKS TWICE
 * --------------------------------
 * Both the rate and the on/off switch multiply — or stop multiplying — every
 * bill from the next order onwards. That is not a thing to change on a stray
 * tap.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

const field =
  'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

/** What a bill of Rs. 1,000 would carry at this rate, so the number means something. */
function onAThousand(rate) {
  const tax = (1000 * (Number(rate) || 0)) / 100;
  return `Rs. 1,000 of food would carry Rs. ${tax.toFixed(2)} of tax.`;
}

export default function TaxSettingsPage() {
  const queryClient = useQueryClient();
  const can = useAuth((s) => s.can);

  const [editing, setEditing] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'tax-rules'],
    queryFn: () => api.get('/admin/tax-rules'),
  });

  /**
   * Whether an edit here would survive. A branch pulls its tax rules from head
   * office, so a rate changed on it is shown, used for the rest of the day, and
   * quietly replaced at the next sync — the same trap the menu screen guards
   * against. Where this machine owns its rules — head office, or a single shop —
   * everything is editable.
   */
  const sync = useQuery({
    queryKey: ['sync', 'status'],
    queryFn: () => api.get('/sync/status'),
    staleTime: 60_000,
  });
  const receivesFromHeadOffice = sync.data?.cloud?.configured === true;
  const canEdit = sync.isSuccess && !receivesFromHeadOffice && can('settings.update.settings');

  /**
   * The rule for a tender, whether it is switched on or off.
   *
   * A rule set up for the tender wins; a shop that has not split its rates yet
   * has one 'all' rule that stands for both. A turned-off rule is deliberately
   * still found here — otherwise the switch that turned it off would vanish
   * with it, leaving no way to turn it back on. The rules come back with the
   * active ones first, so a live rule is preferred where a tender somehow has
   * both.
   */
  const rules = data?.rules ?? [];
  const forMethod = (method) =>
    rules.find((r) => r.payment_method === method) ??
    rules.find((r) => r.payment_method === 'all') ??
    null;
  const cash = forMethod('cash');
  const card = forMethod('card');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'tax-rules'] });

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="mb-4">
        <h1 className="font-semibold text-xl text-ink">Sales tax</h1>
        <p className="mt-0.5 text-sm text-ink-2">
          The rate charged on every bill, after any discount. Cash and card are taxed separately, so
          each has its own rate and its own switch; an online payment is taxed like cash. Turn a
          tender off and its bills carry no tax and print none.
        </p>
      </div>

      {receivesFromHeadOffice && (
        <p className="mb-4 rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
          This branch receives its tax rates from head office. A change made here would be replaced
          at the next sync, so they are read-only — change them on the head-office system and they
          will reach every branch.
        </p>
      )}

      {isLoading && <Spinner label="Reading the tax rules" />}

      {isError && (
        <p role="alert" className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error?.message ?? 'Those could not be read.'}
        </p>
      )}

      {data && (
        <>
          {editing && (
            <RateForm
              rule={editing}
              onClose={() => setEditing(null)}
              onSaved={() => {
                refresh();
                setEditing(null);
              }}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <RateCard
              label="Tax on cash"
              hint="Cash and online payments."
              rule={cash}
              canEdit={canEdit}
              onEdit={() => setEditing(cash)}
              onChanged={refresh}
            />
            <RateCard
              label="Tax on card"
              hint="Card (digital) payments."
              rule={card}
              canEdit={canEdit}
              onEdit={() => setEditing(card)}
              onChanged={refresh}
            />
          </div>

          {!canEdit && !receivesFromHeadOffice && (
            <p className="mt-3 text-sm text-ink-3">
              {/* Said plainly rather than by hiding the buttons with no reason.
                  Not shown on a branch, where the message above already explains
                  why the rates cannot be changed here. */}
              Only an owner can change or turn off a tax rate.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** One tender's rate, whether it is on, and the ways to change either. */
function RateCard({ label, hint, rule, canEdit, onEdit, onChanged }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const active = Boolean(rule?.is_active);

  const toggle = useMutation({
    mutationFn: () => api.put(`/admin/tax-rules/${rule.id}`, { isActive: !active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tax-rules'] });
      setConfirming(false);
      onChanged?.();
    },
  });

  return (
    <div className="rounded-xl border border-rule bg-surface p-5">
      <p className="text-sm text-ink-3">{label}</p>
      <p
        className={`mt-1 font-semibold text-2xl tabular-nums ${active ? 'text-ink' : 'text-ink-3'}`}
      >
        {rule ? (active ? `${rule.rate}%` : 'Off') : 'Not set'}
      </p>
      <p className="mt-1 text-xs text-ink-3">{hint}</p>

      {rule ? (
        active ? (
          <p className="mt-2 text-xs text-ink-3">{onAThousand(rule.rate)}</p>
        ) : (
          <p className="mt-2 text-xs text-warn">
            Turned off — these bills carry no tax and print none.
          </p>
        )
      ) : (
        <p className="mt-2 text-xs text-warn">
          No rule for this tender, so nothing is charged on it.
        </p>
      )}

      {canEdit && rule && (
        <>
          {confirming ? (
            <div className="mt-3 rounded-lg border border-warn/40 bg-warn-bg px-3.5 py-3">
              <p className="text-sm text-warn">
                {active
                  ? 'Stop charging this tax? From the next order these bills show no tax and print none. Bills already taken are not affected.'
                  : `Charge ${rule.rate}% on these bills again? It takes effect from the next order.`}
              </p>
              {toggle.isError && (
                <p role="alert" className="mt-2 text-sm text-bad">
                  {toggle.error?.message ?? 'That did not save.'}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  variant={active ? 'danger' : 'primary'}
                  busy={toggle.isPending}
                  onClick={() => toggle.mutate()}
                >
                  {active ? 'Turn tax off' : 'Turn tax on'}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(false)}>
                  Go back
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-3">
              <Button
                variant={active ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => setConfirming(true)}
              >
                {active ? 'Turn off tax' : 'Turn on tax'}
              </Button>
              <button
                type="button"
                onClick={onEdit}
                className="text-sm text-ink-2 underline hover:text-ink"
              >
                Change rate
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RateForm({ rule, onClose, onSaved }) {
  const [rate, setRate] = useState(String(rule.rate));
  const [name, setName] = useState(rule.name);
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/tax-rules/${rule.id}`, {
        name: name.trim(),
        rate: Number(rate),
      }),
    onSuccess: onSaved,
  });

  const value = Number(rate);
  const sane = Number.isFinite(value) && value >= 0 && value <= 100 && name.trim();
  const changed = value !== Number(rule.rate) || name.trim() !== rule.name;

  return (
    <div className="mb-4 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-4 font-medium text-ink">{rule.name}</h3>

      <div className="mb-4 flex flex-wrap gap-3">
        <label className="min-w-56 flex-1">
          <span className="mb-1.5 block text-xs text-ink-3">What it is called</span>
          <input
            aria-label="Rule name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={field}
          />
          <span className="mt-1 block text-xs text-ink-3">Printed on every receipt.</span>
        </label>

        <label className="w-36">
          <span className="mb-1.5 block text-xs text-ink-3">Rate</span>
          <input
            aria-label="Tax rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className={`${field} tabular-nums`}
          />
        </label>
      </div>

      {/* The rate said back as money, because a percentage on its own is easy
          to mistype by a factor of ten and hard to sanity-check. */}
      {sane && <p className="mb-4 text-sm text-ink-3">{onAThousand(value)}</p>}

      {!sane && (
        <p className="mb-4 text-sm text-warn">
          A tax rate has to be a number between 0 and 100, and the rule needs a name.
        </p>
      )}

      {save.isError && (
        <p className="mb-3 text-sm text-bad">{save.error?.message ?? 'That did not save.'}</p>
      )}

      {confirming ? (
        <div className="rounded-lg border border-warn/40 bg-warn-bg px-3.5 py-3">
          <p className="text-sm text-warn">
            {/*
              Asked twice on purpose. This multiplies every bill from the next
              order onwards, and nothing on the till will look wrong afterwards
              — it will simply charge a different amount.
            */}
            This changes what every customer is charged from the next order onwards. Bills already
            taken are not affected.
          </p>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : `Yes, charge ${value}%`}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Go back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button onClick={() => setConfirming(true)} disabled={!sane || !changed}>
            Save
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
